import { RefObject, useEffect, useRef, useState } from "react";

interface ReturnProps {
  /** Ref to be attached to the element being observed for overflow */
  textRef: RefObject<HTMLElement | null>;

  /** Boolean indicating whether the element's content is overflowing */
  isOverflowing: boolean;
}

/**
 * Returns a ref and a boolean indicating whether the element's content overflows.
 * This can be useful for conditionally displaying tooltips or other UI elements based on content overflow
 */
export function useIsOverflowing<T extends HTMLElement>(): ReturnProps {
  const textRef = useRef<T>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) {
      return;
    }

    function checkOverflow(): void {
      if (!el) {
        return;
      }
      setIsOverflowing(
        el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight
      );
    }

    // Check on initial mount
    checkOverflow();

    // Re-check when the element resizes
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);

    // Re-check when text content changes (e.g., async data load)
    const mutationObserver = new MutationObserver(checkOverflow);
    mutationObserver.observe(el, {
      /* eslint-disable @typescript-eslint/naming-convention -- Comes from external package */
      childList: true,
      characterData: true,
      subtree: true,
      /* eslint-enable @typescript-eslint/naming-convention */
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return { textRef, isOverflowing };
}
